import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../common/api/ApiRequestAdapter";
import { apiService } from "../../common/api/apiService";
import { HttpMethod } from "../../common/api/http";
import { Role } from "../../common/security/enums";
import {
  EntityIdObject,
  EntityObject,
  SearchPageRequest,
  SearchPageResult,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../common/types";
import {
  AdminCreateUpdateAgentUserAccount,
  AdminCreateUserWithAgentUserAccounts,
  AdminUpdateUser,
  AgentUserAccount,
  AgentUserInvitation,
  AgentUserProfile,
  ClientUserProfile,
  ConfirmUser,
  CreateUserTotpDevice,
  CreateUserTotpDeviceSecret,
  DeleteUserTotpDevice,
  GetUsersByAgentRequest,
  PasswordReset,
  RequestPasswordReset,
  RequestUserEmailUpdate,
  RequestUserPhoneUpdate,
  UpdateUserEmail,
  UpdateUserPhone,
  UpdateUserTotpDevice,
  UserAdminView,
  UserSendTotpCodeRequest,
  UserTotpDevice,
  UserTotpDeviceSecret,
  UserUpdatePassword,
} from "./types";

const ADMIN_FILTER_USERS = ApiRequestAdapter.create("/users", HttpMethod.GET);
const ADMIN_GET_USERS_BY_AGENT = ApiRequestAdapter.create("/users", HttpMethod.GET);
const AGENT_GET_USER_INVITATIONS_BY_AGENT = ApiRequestAdapter.create("/users/invitations", HttpMethod.GET);
const ADMIN_CREATE_USER_WITH_AGENT_USER_ACCOUNTS = ApiRequestAdapter.create("/users", HttpMethod.POST);
const ADMIN_GET_USER = ApiRequestAdapter.create("/users/{0}", HttpMethod.GET);
const USER_SEND_TOTP_CODE_VIA_SMS = ApiRequestAdapter.create("/users/{0}/send-totp-code-via-sms", HttpMethod.POST);
const USER_CONFIRM_USER = ApiRequestAdapter.create("/users/{0}/confirm", HttpMethod.PUT);
const ADMIN_UPDATE_USER = ApiRequestAdapter.create("/users/{0}", HttpMethod.PUT);
const ADMIN_RESEND_USER_CONFIRM_LINK = ApiRequestAdapter.create("/users/{0}/resend-confirm-link", HttpMethod.PUT);
const AGENT_RESEND_USER_INVITATION = ApiRequestAdapter.create("/users/{0}/resend-invitation", HttpMethod.PUT);
const USER_GET_USER_PROFILE = ApiRequestAdapter.create("/users/{0}/profile", HttpMethod.GET);
const USER_UPDATE_PASSWORD = ApiRequestAdapter.create("/users/{0}/password", HttpMethod.PUT);
const USER_REQUEST_EMAIL_UPDATE = ApiRequestAdapter.create("/users/{0}/request-email-update", HttpMethod.POST);
const USER_UPDATE_EMAIL = ApiRequestAdapter.create("/users/{0}/update-email", HttpMethod.PUT);
const USER_REQUEST_PHONE_UPDATE = ApiRequestAdapter.create("/users/{0}/request-phone-update", HttpMethod.POST);
const USER_UPDATE_PHONE = ApiRequestAdapter.create("/users/{0}/update-phone", HttpMethod.PUT);
const USER_REQUEST_PASSWORD_RESET = ApiRequestAdapter.create("/users/request-password-reset", HttpMethod.POST);
const USER_RESET_PASSWORD = ApiRequestAdapter.create("/users/{0}/reset-password", HttpMethod.PUT);
const ADMIN_ADD_AGENT_USER_ACCOUNT = ApiRequestAdapter.create("/users/{0}/agent-accounts", HttpMethod.POST);
const ADMIN_UPDATE_AGENT_USER_ACCOUNT = ApiRequestAdapter.create("/users/{0}/agent-accounts/{1}", HttpMethod.PUT);
const ADMIN_DELETE_AGENT_USER_ACCOUNT = ApiRequestAdapter.create("/users/{0}/agent-accounts/{1}", HttpMethod.DELETE);
const USER_GET_USER_TOTP_DEVICES = ApiRequestAdapter.create("/users/{0}/totp-devices", HttpMethod.GET);
const USER_CREATE_USER_TOTP_DEVICE_SECRET = ApiRequestAdapter.create("/users/{0}/totp-devices/secret", HttpMethod.POST);
const USER_CREATE_USER_TOTP_DEVICE = ApiRequestAdapter.create("/users/{0}/totp-devices", HttpMethod.POST);
const USER_UPDATE_USER_TOTP_DEVICE = ApiRequestAdapter.create("/users/{0}/totp-devices/{1}", HttpMethod.PUT);
const USER_DELETE_USER_TOTP_DEVICE = ApiRequestAdapter.create("/users/{0}/totp-devices/{1}/delete", HttpMethod.POST);

export const userApi = {
  adminFilterUsers: (request: SearchPageRequest): AxiosPromise<SearchPageResult<UserAdminView>> => {
    return apiService.get(ADMIN_FILTER_USERS.url, { params: request });
  },
  adminGetUsersByAgent: (request: GetUsersByAgentRequest): AxiosPromise<UserAdminView[]> => {
    return apiService.get(ADMIN_GET_USERS_BY_AGENT.url, { params: request });
  },
  agentGetUserInvitationsByAgent: (request: GetUsersByAgentRequest): AxiosPromise<AgentUserInvitation[]> => {
    return apiService.get(AGENT_GET_USER_INVITATIONS_BY_AGENT.url, { params: request });
  },
  adminCreateUserWithAgentUserAccounts: (
    request: AdminCreateUserWithAgentUserAccounts,
  ): AxiosPromise<UserAdminView> => {
    return apiService.post(ADMIN_CREATE_USER_WITH_AGENT_USER_ACCOUNTS.url, request, { params: { role: Role.AGENT } });
  },
  adminGetUser: (request: EntityIdObject): AxiosPromise<UserAdminView> => {
    return apiService.get(ADMIN_GET_USER.getParametrizedUrl(request.id));
  },
  userSendTotpCodeViaSms: (request: EntityObject<UserSendTotpCodeRequest>): AxiosPromise => {
    return apiService.post(USER_SEND_TOTP_CODE_VIA_SMS.getParametrizedUrl(request.id), request.object);
  },
  userConfirmUser: (request: EntityObject<ConfirmUser>): AxiosPromise => {
    return apiService.put(USER_CONFIRM_USER.getParametrizedUrl(request.id), request.object);
  },
  adminUpdateUser: (request: EntityObject<AdminUpdateUser>): AxiosPromise<UserAdminView> => {
    return apiService.put(ADMIN_UPDATE_USER.getParametrizedUrl(request.id), request.object);
  },
  adminResendUserConfirmLink: (request: EntityIdObject): AxiosPromise<UserAdminView> => {
    return apiService.put(ADMIN_RESEND_USER_CONFIRM_LINK.getParametrizedUrl(request.id));
  },
  agentResendUserInvitation: (request: EntityIdObject): AxiosPromise<AgentUserInvitation> => {
    return apiService.put(AGENT_RESEND_USER_INVITATION.getParametrizedUrl(request.id));
  },
  userGetUserProfile: (request: EntityIdObject): AxiosPromise<AgentUserProfile | ClientUserProfile> => {
    return apiService.get(USER_GET_USER_PROFILE.getParametrizedUrl(request.id));
  },
  userUpdatePassword: (
    request: EntityObject<UserUpdatePassword>,
  ): AxiosPromise<AgentUserProfile | ClientUserProfile> => {
    return apiService.put(USER_UPDATE_PASSWORD.getParametrizedUrl(request.id), request.object);
  },
  userRequestEmailUpdate: (
    request: EntityObject<RequestUserEmailUpdate>,
  ): AxiosPromise<AgentUserProfile | ClientUserProfile> => {
    return apiService.post(USER_REQUEST_EMAIL_UPDATE.getParametrizedUrl(request.id), request.object);
  },
  userUpdateEmail: (request: EntityObject<UpdateUserEmail>): AxiosPromise => {
    return apiService.put(USER_UPDATE_EMAIL.getParametrizedUrl(request.id), request.object);
  },
  userRequestPhoneUpdate: (
    request: EntityObject<RequestUserPhoneUpdate>,
  ): AxiosPromise<AgentUserProfile | ClientUserProfile> => {
    return apiService.post(USER_REQUEST_PHONE_UPDATE.getParametrizedUrl(request.id), request.object);
  },
  userUpdatePhone: (request: EntityObject<UpdateUserPhone>): AxiosPromise<AgentUserProfile | ClientUserProfile> => {
    return apiService.put(USER_UPDATE_PHONE.getParametrizedUrl(request.id), request.object);
  },
  requestPasswordReset: (request: RequestPasswordReset): AxiosPromise => {
    return apiService.post(USER_REQUEST_PASSWORD_RESET.url, request);
  },
  resetPassword: (request: EntityObject<PasswordReset>): AxiosPromise => {
    return apiService.put(USER_RESET_PASSWORD.getParametrizedUrl(request.id), request.object);
  },
  adminAddAgentUserAccount: (
    request: EntityObject<AdminCreateUpdateAgentUserAccount>,
  ): AxiosPromise<AgentUserAccount> => {
    return apiService.post(ADMIN_ADD_AGENT_USER_ACCOUNT.getParametrizedUrl(request.id), request.object);
  },
  adminUpdateAgentUserAccount: (
    request: TwoLevelEntityObject<AdminCreateUpdateAgentUserAccount>,
  ): AxiosPromise<AgentUserAccount> => {
    return apiService.put(ADMIN_UPDATE_AGENT_USER_ACCOUNT.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  adminDeleteAgentUserAccount: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(ADMIN_DELETE_AGENT_USER_ACCOUNT.getParametrizedUrl(request.id1, request.id2));
  },
  userGetUserTotpDevices: (request: EntityIdObject): AxiosPromise<UserTotpDevice[]> => {
    return apiService.get(USER_GET_USER_TOTP_DEVICES.getParametrizedUrl(request.id));
  },
  userCreateUserTotpDeviceSecret: (
    request: EntityObject<CreateUserTotpDeviceSecret>,
  ): AxiosPromise<UserTotpDeviceSecret> => {
    return apiService.post(USER_CREATE_USER_TOTP_DEVICE_SECRET.getParametrizedUrl(request.id), request.object);
  },
  userCreateUserTotpDevice: (request: EntityObject<CreateUserTotpDevice>): AxiosPromise<UserTotpDevice> => {
    return apiService.post(USER_CREATE_USER_TOTP_DEVICE.getParametrizedUrl(request.id), request.object);
  },
  userUpdateUserTotpDevice: (request: TwoLevelEntityObject<UpdateUserTotpDevice>): AxiosPromise<UserTotpDevice> => {
    return apiService.put(USER_UPDATE_USER_TOTP_DEVICE.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  userDeleteUserTotpDevice: (request: TwoLevelEntityObject<DeleteUserTotpDevice>): AxiosPromise => {
    return apiService.post(USER_DELETE_USER_TOTP_DEVICE.getParametrizedUrl(request.id1, request.id2), request.object);
  },
};
